"use client";

import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { type ReactNode } from "react";
import { languageLabels } from "~/lib/languages/utilities";

function setLocaleCookie(locale: string) {
  // biome-ignore lint/suspicious/noDocumentCookie: ui_locale is a non-sensitive client-readable cookie
  document.cookie = `ui_locale=${locale};path=/;max-age=31536000;samesite=lax`;
}

/**
 * ログイン画面限定のアクセントカラー(サイト全体の`--prnc-primary`とは独立、
 * Prysm/Proovaの ACCENT 定数と同じ設計 — 姉妹プロダクトごとに異なる単色を
 * ブランドパネルのロゴ・見出し・CTAにのみ適用し、彩度の低い業務画面用トークン
 * では出せない「映え」を確保する。2026-08-10導入)。
 */
const ACCENT = "oklch(0.58 0.16 55)"; // WCAG AA(4.5:1)適合のため2026-08-18に明度調整(docs/design-system.md §1.2)

export const authInputClass =
  "lf-input box-border w-full rounded-[10px] border border-disabled bg-surface px-3.75 py-3.25 font-sans text-[15px] text-fg outline-none";

export const authLabelClass = "mb-2 block font-medium text-label text-muted";

export const authBtnClass =
  "lf-btn w-full cursor-pointer rounded-[10px] border-none p-3.5 font-medium text-[15.5px] text-surface tracking-[0.04em]";

export const authLinkClass = "lf-link";

export const authCheckboxClass = "lf-checkbox";

export const AUTH_PAGE_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700;900&family=IBM+Plex+Mono:wght@400;500&display=swap');
  * { box-sizing: border-box; }
  html, body { margin: 0; height: 100%; }
  .lf-input::placeholder { color: var(--color-placeholder); }
  .lf-input:focus { border-color: ${ACCENT}; box-shadow: 0 0 0 3px rgba(0,0,0,0.04); }
  .lf-btn { background: ${ACCENT}; }
  .lf-btn:hover:not(:disabled) { filter: brightness(1.07); }
  .lf-link { color: ${ACCENT}; }
  .lf-checkbox { accent-color: ${ACCENT}; }
  @media (max-width: 820px) { .lf-brand { display: none !important; } }
`;

function useLangSelector() {
  const router = useRouter();
  const locale = useLocale();

  return (
    <div className="absolute top-8 right-9 flex select-none items-center gap-1 rounded-lg border border-disabled bg-surface px-3.5 py-2 text-label text-muted">
      <select
        value={locale}
        onChange={(e) => {
          setLocaleCookie(e.target.value);
          router.refresh();
        }}
        className="cursor-pointer appearance-none border-none bg-transparent font-sans text-label text-muted outline-none"
      >
        {Object.entries(languageLabels).map(([code, label]) => (
          <option key={code} value={code}>
            {label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none text-caption text-placeholder">
        ▾
      </span>
    </div>
  );
}

function BrandPanel() {
  const t = useTranslations("AuthPage");

  return (
    <div className="lf-brand relative flex flex-[1_1_50%] flex-col justify-between overflow-hidden bg-fg px-14 pt-14 pb-13 text-surface">
      <div
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }}
        className="pointer-events-none absolute inset-0"
      />
      <div
        style={{
          background: `radial-gradient(55% 45% at 72% 28%, ${ACCENT} 0%, transparent 66%)`,
        }}
        className="pointer-events-none absolute inset-0 opacity-42"
      />

      <div className="relative flex items-center gap-3.25">
        <div
          className="size-5.5 shrink-0 rotate-45 rounded-[5px]"
          style={{ background: ACCENT }}
        />
        <span className="font-medium text-[21px] tracking-[-0.01em]">
          Proomi
        </span>
      </div>

      <div className="relative max-w-[470px]">
        <div
          style={{ fontFamily: "'IBM Plex Mono', monospace", color: ACCENT }}
          className="mb-5.5 font-medium text-[13px] tracking-[0.16em]"
        >
          {t("brandTagline")}
        </div>
        <h1
          style={{ whiteSpace: "pre-line" }}
          className="mb-6 font-medium text-[46px] leading-[1.3] tracking-[-0.01em]"
        >
          {t("brandHeadline")}
        </h1>
        <p className="m-0 max-w-[430px] text-body-lg text-surface/60 leading-[1.85]">
          {t("brandSubtext")}
        </p>
      </div>

      <div className="relative flex flex-col gap-4">
        {[
          t("featureAiInterview"),
          t("featureConsistent"),
          t("featureReport"),
        ].map((feat) => (
          <div
            key={feat}
            className="flex items-center gap-3 text-[14.5px] text-surface/76"
          >
            <span
              className="size-1.5 shrink-0 rounded-full"
              style={{ background: ACCENT }}
            />
            {feat}
          </div>
        ))}
      </div>
    </div>
  );
}

type Props = { children: ReactNode };

/**
 * Shared two-column auth layout (brand panel + form) used by both the
 * tenant-staff login flow (/login/*) and the participant-portal login flow
 * (/portal/[organizationId]/*) so the two stay visually in sync.
 */
export function AuthShell({ children }: Props) {
  const langSelector = useLangSelector();

  return (
    <>
      <style>{AUTH_PAGE_STYLE}</style>
      <div className="flex h-screen w-full overflow-hidden bg-surface font-sans text-fg">
        <BrandPanel />
        <div className="relative box-border flex flex-[1_1_50%] items-center justify-start bg-surface px-18 py-14">
          {langSelector}
          <div className="box-border w-full max-w-[380px]">{children}</div>
        </div>
      </div>
    </>
  );
}
